import React, { useEffect, useMemo, useState } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaSignInAlt,
  FaSignOutAlt,
  FaTimes,
  FaUserCircle,
  FaUserPlus,
} from "react-icons/fa";
import { ACCENT_STYLES, getBadgeValue } from "../navbar4Config";

const getUserName = (user) => {
  if (!user) return "Visiteur";

  const firstName = user.prenom || user.first_name || user.firstname || "";
  const lastName = user.nom || user.last_name || user.lastname || "";

  return (
    `${firstName} ${lastName}`.trim() ||
    user.username ||
    user.email ||
    "Utilisateur"
  );
};

const getRoleLabel = (user) => {
  const roleId = Number(user?.role_id);

  if (roleId === 1) return "Administrateur";
  if (roleId === 3) return "Coach";
  if ([5, 6, 7].includes(roleId)) return "Responsable";
  if (roleId === 2) return "Judoka";

  return "Compte utilisateur";
};

const getFallbackAccent = () => ({
  text: "text-sky-700 dark:text-sky-200",
  leftBorder: "border-l-[10px] border-sky-500",
  bg: "bg-sky-600",
  soft: "bg-sky-50 dark:bg-sky-500/10",
  border: "border-sky-200 dark:border-sky-400/20",
  gradient: "from-sky-600 to-blue-600",
  iconSoft: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-200",
});

const Navbar4MobileMenu = ({
  open,
  sections,
  counts,
  onClose,
  onNavigate,
  user,
  onLogout,
}) => {
  const [openSectionId, setOpenSectionId] = useState(sections[0]?.id || null);

  const userName = getUserName(user);
  const roleLabel = getRoleLabel(user);
  const avatar =
    user?.photoURL || user?.photo_url || user?.avatar || "/img/user_avatar.jpg";

  const visibleSections = useMemo(() => sections || [], [sections]);

  useEffect(() => {
    if (!visibleSections.find((section) => section.id === openSectionId)) {
      setOpenSectionId(visibleSections[0]?.id || null);
    }
  }, [visibleSections, openSectionId]);

  useEffect(() => {
    if (open && !openSectionId && visibleSections.length > 0) {
      setOpenSectionId(visibleSections[0].id);
    }
  }, [open, openSectionId, visibleSections]);

  return (
    <div
      className={`fixed inset-0 z-[80] bg-slate-950/70 backdrop-blur-[3px] transition-opacity duration-300 md:hidden ${
        open
          ? "pointer-events-auto opacity-100"
          : "pointer-events-none opacity-0"
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Menu de navigation"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
        aria-label="Fermer le menu"
      />

      <div
        className={`absolute inset-x-0 bottom-0 max-h-[calc(100dvh-14px)] overflow-hidden rounded-t-[2rem] bg-[#f8fafc] shadow-2xl transition-transform duration-300 dark:bg-[#070b14] ${
          open ? "translate-y-0" : "translate-y-6"
        }`}
      >
        <div className="flex max-h-[calc(100dvh-14px)] flex-col">
          <header className="relative overflow-hidden border-b border-slate-200 bg-white px-4 pb-4 pt-4 dark:border-white/10 dark:bg-[#0b1220]">
            <div className="absolute -right-10 -top-12 h-32 w-32 rounded-full bg-sky-500/15 blur-3xl" />
            <div className="absolute -bottom-12 left-8 h-28 w-28 rounded-full bg-orange-500/10 blur-3xl" />

            <div className="relative flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 dark:bg-white/10 dark:ring-white/10">
                  <img
                    src="/logo_judocoach_pro_512_2.png"
                    alt="Judo Coach Pro"
                    className="h-full w-full object-contain p-1"
                  />
                </span>

                <div className="min-w-0">
                  <h2 className="truncate font-heading text-[1.85rem] font-bold leading-none tracking-tight text-slate-950 dark:text-white">
                    Judo Coach Pro
                  </h2>

                  <p className="mt-1 text-[0.72rem] font-light capitalize tracking-[0.18em] text-slate-500 dark:text-white/55">
                    Carnet de performances
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition active:scale-95 dark:border-white/10 dark:bg-white/[0.06] dark:text-white"
                aria-label="Fermer le menu"
              >
                <FaTimes />
              </button>
            </div>

            {user ? (
              <div className="relative mt-4 flex items-center gap-3 rounded-[1.4rem] border border-slate-200 bg-slate-50 p-3 shadow-sm dark:border-white/10 dark:bg-white/[0.04]">
                <img
                  src={avatar}
                  alt={userName}
                  className="h-12 w-12 rounded-2xl object-cover ring-2 ring-white dark:ring-white/10"
                />

                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-sm font-bold text-slate-950 dark:text-white">
                    {userName}
                  </p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-white/45">
                    {roleLabel}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => onNavigate("/profile-utilisateur")}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-600 shadow-sm ring-1 ring-slate-200 transition active:scale-95 dark:bg-white/[0.07] dark:text-sky-200 dark:ring-white/10"
                  aria-label="Profil utilisateur"
                >
                  <FaUserCircle />
                </button>
              </div>
            ) : (
              <div className="relative mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onNavigate("/register")}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-3 py-2 text-sm font-bold text-slate-950 shadow-md shadow-orange-500/15 transition active:scale-[0.98]"
                >
                  <FaUserPlus />
                  Créer un compte
                </button>

                <button
                  type="button"
                  onClick={() => onNavigate("/login")}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition active:scale-[0.98] dark:border-white/10 dark:bg-white/[0.06] dark:text-white"
                >
                  <FaSignInAlt />
                  Connexion
                </button>
              </div>
            )}
          </header>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="space-y-3 pb-24">
              {visibleSections.map((section) => {
                const accent =
                  ACCENT_STYLES[section.accent] || getFallbackAccent();

                const isOpen = openSectionId === section.id;

                return (
                  <section
                    key={section.id}
                    className={`overflow-hidden rounded-[1.55rem] border bg-white shadow-sm transition-all dark:bg-[#101827] ${
                      isOpen
                        ? `${accent.border} ${accent.leftBorder}`
                        : "border-slate-200 dark:border-white/10"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenSectionId((prev) =>
                          prev === section.id ? null : section.id,
                        )
                      }
                      className={`flex w-full items-center gap-3 px-4 py-4 text-left transition ${
                        isOpen ? `${accent.soft}` : "bg-white dark:bg-[#101827]"
                      }`}
                    >
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${accent.iconSoft}`}
                      >
                        <span className="text-sm font-bold">
                          {section.items.length}
                        </span>
                      </div>

                      <span className="min-w-0 flex-1">
                        <span
                          className={`block text-[1rem] font-bold uppercase tracking-[0.13em] ${accent.text}`}
                        >
                          {section.title}
                        </span>

                        <span className="mt-1 line-clamp-2 block text-[13px] font-medium leading-5 text-slate-500 dark:text-white/45">
                          {section.subtitle}
                        </span>
                      </span>

                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm ring-1 ring-slate-200 dark:bg-white/[0.07] dark:text-white/60 dark:ring-white/10">
                        {isOpen ? (
                          <FaChevronDown className="text-xs" />
                        ) : (
                          <FaChevronRight className="text-xs" />
                        )}
                      </span>
                    </button>

                    <div
                      className={`grid transition-all duration-300 ${
                        isOpen
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="border-t border-slate-200 bg-slate-50/70 p-2 dark:border-white/10 dark:bg-slate-950/20">
                          {section.items.map((item) => {
                            const Icon = item.icon;
                            const badge = getBadgeValue(item, counts);

                            return (
                              <button
                                key={item.key}
                                type="button"
                                onClick={() =>
                                  onNavigate(item.path, item.roles)
                                }
                                className={`group flex w-full items-center gap-3 rounded-[1.25rem] px-3 py-3.5 text-left transition active:scale-[0.99] ${
                                  item.featured
                                    ? `${accent.soft} ring-1 ${accent.border}`
                                    : "hover:bg-white dark:hover:bg-white/[0.05]"
                                }`}
                              >
                                <span
                                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${accent.iconSoft}`}
                                >
                                  {Icon ? (
                                    <Icon className="text-[15px]" />
                                  ) : null}
                                </span>

                                <span className="min-w-0 flex-1">
                                  <span className="flex min-w-0 items-center gap-2">
                                    <span className="truncate font-heading text-[15px] font-bold text-slate-950 dark:text-white">
                                      {item.label}
                                    </span>

                                    {/* {item.soon && (
                                      <span className="shrink-0 rounded-full bg-slate-950 px-2 py-0.5 text-[10px] font-bold text-white dark:bg-white dark:text-slate-950">
                                        bientôt
                                      </span>
                                    )} */}
                                  </span>

                                  <span className="mt-1 line-clamp-2 block text-[13px] font-medium leading-5 text-slate-500 dark:text-white/45">
                                    {item.description}
                                  </span>
                                </span>

                                {badge > 0 && (
                                  <span className="shrink-0 rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold text-white">
                                    {badge > 99 ? "99+" : badge}
                                  </span>
                                )}

                                <FaChevronRight className="shrink-0 text-xs text-slate-400 transition-transform group-hover:translate-x-1 dark:text-white/35" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </section>
                );
              })}

              {user && (
                <button
                  type="button"
                  onClick={onLogout}
                  className="flex w-full items-center justify-center gap-2 rounded-[1.4rem] border border-red-200 bg-red-50 px-4 py-4 text-sm font-bold text-red-600 shadow-sm transition active:scale-[0.99] dark:border-red-500/20 dark:bg-red-500/10"
                >
                  <FaSignOutAlt />
                  Se déconnecter
                </button>
              )}
            </div>
          </div>

          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#f8fafc] to-transparent dark:from-[#070b14]"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
};

export default Navbar4MobileMenu;
